import imageCompression from "browser-image-compression";
import i18n from "@/lib/i18n";

export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
];
export const MAX_IMAGE_SIZE_BYTES = 15 * 1024 * 1024; // 15 MB pre-compression

const isHeic = (file: File) => {
  const type = (file.type || "").toLowerCase();
  if (type === "image/heic" || type === "image/heif") return true;
  const name = file.name.toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif");
};

export const validateImageFile = (file: File): string | null => {
  const type = (file.type || "").toLowerCase();
  const allowed = ALLOWED_IMAGE_TYPES.includes(type) || isHeic(file) || type.startsWith("image/");
  if (!allowed) return i18n.t("validate.imageFormat");
  if (file.size > MAX_IMAGE_SIZE_BYTES) return i18n.t("validate.imageSize");
  return null;
};

const convertHeicToJpeg = async (file: File): Promise<File> => {
  try {
    const heic2any = (await import("heic2any")).default;
    const blob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.85 });
    const out = Array.isArray(blob) ? blob[0] : blob;
    const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
    return new File([out], newName, { type: "image/jpeg", lastModified: Date.now() });
  } catch (err) {
    console.warn("HEIC conversion failed", err);
    return file;
  }
};

export const compressImage = async (file: File): Promise<File> => {
  let working = file;
  if (isHeic(working)) working = await convertHeicToJpeg(working);
  if (working.size <= 1024 * 1024) return working;
  try {
    const compressed = await imageCompression(working, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      initialQuality: 0.82,
    });
    return new File([compressed], working.name, {
      type: compressed.type || working.type,
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn("Image compression failed, using original", err);
    return working;
  }
};

export const compressImages = async (files: File[]): Promise<File[]> =>
  Promise.all(files.map(compressImage));
